<template>
  <Dialog
    :model-value="dialogVisible"
    title="Internal Notes (Staff Only)"
    dialog-type="primary"
    dialog-size="large"
    title-variant="medium"
    :close-on-overlay="false"
    @update:model-value="(v) => { if (!v) handleClose() }"
    @close="handleClose"
  >
    <template #header-icon>
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20" fill="none">
        <path d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H14C14.55 0 15.0208 0.195833 15.4125 0.5875C15.8042 0.979167 16 1.45 16 2V18C16 18.55 15.8042 19.0208 15.4125 19.4125C15.0208 19.8042 14.55 20 14 20H2ZM2 18H14V2H12V9L9.5 7.5L7 9V2H2V18Z" fill="white"/>
      </svg>
    </template>
    <div class="internal-notes-body">
      <div v-if="isLoading" class="flex items-center justify-center" style="min-height: 400px;">
        <Loading
          :loading="isLoading"
          loadingText="Loading..."
        />
      </div>
      <section v-if="!isLoading && historyNotes.length > 0" class="history-notes-section mb-5">
        <h3 class="history-notes-title mb-3">
          History Notes
        </h3>
        <div
          class="history-notes-scroll-container"
          :class="{
            'card-expanded': expandedCardId,
            'single-card': historyNotes.length === 1
          }"
        >
          <HistoryNoteCard
            v-for="(note, index) in historyNotes"
            :ref="`noteCard_${index}`"
            :key="note.id"
            :id="note.id"
            :patient-id="patientId"
            :index="index"
            :total-count="historyNotes.length"
            :author="note.author"
            :time="note.time"
            :text="note.text"
            :is-expanded="expandedCardId === note.id"
            :comments-count="note.commentsCount || 0"
            :reply-count="note.replyCount || 0"
            :customer-name-map="customerNameMap"
            :customer-list="customerList"
            @expand="handleCardExpand"
            @update="fetchNotes"
          />
        </div>
      </section>

      <div v-if="!isLoading && historyNotes.length > 0" class="internal-notes-divider"></div>

      <add-internal-note-form
        v-if="!isLoading"
        ref="addNoteForm"
        v-model="noteText"
        :customer-name-map="customerNameMap"
        :customer-list="customerList"
        @cancel="handleCancel"
        @submit="handleSubmit"
      />
    </div>
  </Dialog>
</template>

<script>
import AddInternalNoteForm from './internalNoteComponents/AddInternalNoteForm.vue';
import HistoryNoteCard from './internalNoteComponents/HistoryNoteCard.vue';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import { notify } from '@/utils/notify';
import { getInternalNotesApi, createInternalNoteApi, getJwtTokenCustomerList } from '@/api/internalNotes';
import { mapState } from 'vuex';

// Module-level cache for customer name map (shared across all dialog instances)
let cachedCustomerNameMap = null;
let cachedCustomerList = null;
let cachedToken = null;

// Export a function to clear the cache (can be called from login store)
export function clearCustomerCache() {
  cachedCustomerNameMap = null;
  cachedCustomerList = null;
  cachedToken = null;
}

export default {
  name: 'InternalNotesDialog',
  components: {
    AddInternalNoteForm,
    HistoryNoteCard,
    Loading,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    patientId: {
      type: String,
      default: null,
    },
    initialNoteId: {
      type: String,
      default: null,
    },
    initialShowComments: {
      type: Boolean,
      default: false,
    },
    initialEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'update:visible', 'submit'],
  data() {
    return {
      noteText: '',
      expandedCardId: null,
      historyNotes: [],
      isLoading: false,
      customerNameMap: {}, // { "999997": "Vibrant IT3333", ... }
      customerList: [], // [{ customer_id: "999997", name: "Vibrant IT3333" }, ...]
      pendingNoteId: null, // Track note to expand after fetch
      pendingShowComments: false, // Track whether to show comments after expand
      pendingEditMode: false // Track whether to enable edit mode after expand
    };
  },
  computed: {
    ...mapState('login', ['rc_user_id', 'userInfo']),

    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
  },
  watch: {
    // When dialog opens, fetch notes from API
    async visible(newVal) {
      if (newVal && this.patientId) {
        // Store initial state to apply after notes are fetched
        this.pendingNoteId = this.initialNoteId;
        this.pendingShowComments = this.initialShowComments;
        this.pendingEditMode = this.initialEditMode;

        // Load customer name map first, then fetch notes
        await this.loadCustomerNameMap();
        await this.fetchNotes();

        // Apply initial state after notes are fetched
        this.applyInitialState();
      } else if (!newVal) {
        // Reset state when dialog closes
        this.expandedCardId = null;
        this.pendingNoteId = null;
        this.pendingShowComments = false;
        this.pendingEditMode = false;
      }
    },
  },
  mounted() {
    // Use capture phase so outside click is handled BEFORE card click
    document.addEventListener('click', this.handleClickOutside, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside, true);
  },
  methods: {
    // Load customer name map from API (with module-level caching)
    async loadCustomerNameMap() {
      // Get current token from sessionStorage
      const currentToken = sessionStorage.getItem('token');

      // Check if cache is valid for current token
      if (cachedCustomerNameMap && cachedCustomerList && cachedToken === currentToken) {
        this.customerNameMap = cachedCustomerNameMap;
        this.customerList = cachedCustomerList;
        return;
      }

      // Clear cache if token has changed
      if (cachedToken !== currentToken) {
        clearCustomerCache();
      }

      try {
        const response = await getJwtTokenCustomerList();

        // Convert [{ "999997": "Vibrant IT3333" }] to { "999997": "Vibrant IT3333" }
        const customerNameMap = response.reduce((acc, item) => {
          return { ...acc, ...item };
        }, {});

        // Fallback: add common mappings that might be missing from API
        const fallbackMappings = {
          '999997': 'Vibrant IT3333'
        };
        const finalCustomerNameMap = { ...customerNameMap, ...fallbackMappings };

        // Convert to array format for MentionTextarea: [{ customer_id: "999997", name: "Vibrant IT3333" }]
        const customerList = response.flatMap(item => {
          return Object.entries(item).map(([customer_id, name]) => ({
            customer_id,
            name
          }));
        });

        // Cache the results at module level along with the current token
        cachedCustomerNameMap = finalCustomerNameMap;
        cachedCustomerList = customerList;
        cachedToken = currentToken;

        this.customerNameMap = finalCustomerNameMap;
        this.customerList = customerList;
      } catch (error) {
        console.error('Failed to load customer name map:', error);
        // Continue with empty map on error
      }
    },

    // Map backend notes data to frontend format
    mapNotesToFrontendFormat(notes) {
      return notes.map(note => {
        // Format the updated_at timestamp
        const formattedTime = this.formatTimestamp(note.updated_at);

        // Get author ID from response (backend returns author_customer_id)
        const authorId = note.author_customer_id || note.author?.customer_id;

        // Display author name from customer name map, fallback to customer_id
        const authorName = this.customerNameMap[authorId] || authorId;

        return {
          id: note.id,
          author: authorName,
          time: formattedTime,
          text: note.content,
          commentsCount: note.unread_reply_count || 0,
          replyCount: note.reply_count || 0
        };
      });
    },

    // Format ISO timestamp to human-readable format
    formatTimestamp(isoString) {
      if (!isoString) return '';

      const date = new Date(isoString);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const noteDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      // Calculate difference in days
      const diffTime = today - noteDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      // Format time as HH:MM
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const timeStr = `${hours}:${minutes}`;

      // Format date based on how recent it is
      if (diffDays === 0) {
        return `Today ${timeStr}`;
      } else if (diffDays === 1) {
        return `Yesterday ${timeStr}`;
      } else {
        // Show date format instead of weekday for older notes
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        const day = date.getDate();
        const dayWithSuffix = this.addDaySuffix(day);
        const year = date.getFullYear();
        return `${month} ${dayWithSuffix}, ${year} ${timeStr}`;
      }
    },

    // Add ordinal suffix to day (1st, 2nd, 3rd, 4th, etc.)
    addDaySuffix(day) {
      if (day > 3 && day < 21) return day + 'th';
      switch (day % 10) {
        case 1: return day + 'st';
        case 2: return day + 'nd';
        case 3: return day + 'rd';
        default: return day + 'th';
      }
    },

    // Fetch notes from backend API
    async fetchNotes() {
      if (!this.patientId) {
        console.warn('No patientId provided');
        return;
      }

      this.isLoading = true;

      try {
        const response = await getInternalNotesApi(this.patientId);

        // Backend returns { data: [...], meta: {...} }
        if (response && response.data) {
          this.historyNotes = this.mapNotesToFrontendFormat(response.data);
        } else {
          this.historyNotes = [];
        }

      } catch (error) {
        console.error('Error fetching notes:', error);
        // Show error notification
        notify({
          title: 'Error',
          message: 'Failed to load internal notes',
          type: 'error',
          duration: 3000
        });
        this.historyNotes = [];
      } finally {
        this.isLoading = false;
      }
    },

    handleCardExpand(cardId) {
      // Only expand, don't toggle - collapse happens when clicking outside
      this.expandedCardId = cardId;
    },

    handleClickOutside(event) {
      // If no card is expanded, do nothing
      if (!this.expandedCardId) return;

      // Get all card elements in the scroll container
      const scrollContainer = document.querySelector('.history-notes-scroll-container');
      if (!scrollContainer) return;

      // Check if click is inside any card
      const clickedCard = event.target.closest('.history-note-card');
      if (clickedCard) {
        // Click is on a card, let the card's own handler deal with it
        return;
      }

      // Click is outside all cards, collapse
      this.expandedCardId = null;
    },
    // Apply initial state (expand note, show comments, edit mode) after notes are fetched
    applyInitialState() {
      if (!this.pendingNoteId) return;

      // Find the note in historyNotes
      const noteIndex = this.historyNotes.findIndex(note => note.id === this.pendingNoteId);

      if (noteIndex !== -1) {
        // Expand the note card
        this.expandedCardId = this.pendingNoteId;

        // Use nextTick to wait for the card to expand, then handle comments/edit
        this.$nextTick(() => {
          const noteCard = this.$refs[`noteCard_${noteIndex}`];
          if (noteCard) {
            // Show comments if requested
            if (this.pendingShowComments) {
              noteCard.showComments = true;
            }
            // Enable edit mode if requested
            if (this.pendingEditMode) {
              noteCard.isEditing = true;
              noteCard.editText = noteCard.text || this.historyNotes[noteIndex].text;
            }

            // Scroll to center after expansion animation completes (250ms + buffer)
            setTimeout(() => {
              this.scrollToNoteCenter(noteCard);
            }, 350);
          }
        });
      }

      // Clear pending state
      this.pendingNoteId = null;
      this.pendingShowComments = false;
      this.pendingEditMode = false;
    },

    // Scroll note card to center of the scroll container
    scrollToNoteCenter(noteCardElement) {
      // Dialog is teleported to body, so query from document directly.
      const scrollContainer = document.querySelector(
        '.general-dialog .history-notes-scroll-container'
      );

      if (!scrollContainer) {
        return;
      }

      if (!noteCardElement) return;

      // Get the card's DOM element - try multiple approaches
      let cardEl = null;

      // Try 1: Vue 2/3 $el property
      if (noteCardElement.$el) {
        cardEl = noteCardElement.$el;
      }
      // Try 2: The element itself might be a DOM node
      else if (noteCardElement.nodeType === 1) {
        cardEl = noteCardElement;
      }
      // Try 3: Find by class in the scroll container
      else {
        // Find all note cards and match by ref index
        const noteCards = scrollContainer.querySelectorAll('.history-note-card');
        // Find the one that's expanded (has is-expanded class)
        for (let card of noteCards) {
          if (card.classList.contains('is-expanded') || card.querySelector('.expanded-content')) {
            cardEl = card;
            break;
          }
        }
      }

      if (!cardEl) {
        return;
      }

      // Calculate center position
      const containerWidth = scrollContainer.offsetWidth;
      const cardLeft = cardEl.offsetLeft;
      const cardWidth = cardEl.offsetWidth;

      // Scroll so the card is centered
      const targetScrollLeft = cardLeft - (containerWidth / 2) + (cardWidth / 2);

      scrollContainer.scrollTo({
        left: targetScrollLeft,
        behavior: 'auto'
      });
    },

    handleClose() {
      this.$emit('close');
      this.$emit('update:visible', false);
    },
    handleCancel() {
      this.noteText = '';
      this.handleClose();
    },
    extractMentionedCustomerIds(content = '') {
      if (!content) return [];

      const mentionRegex = /@doctor_(\d+)_\w+/g;
      const mentionedCustomerIds = new Set();

      let match = mentionRegex.exec(content);
      while (match) {
        const customerId = Number.parseInt(match[1], 10);
        if (Number.isInteger(customerId)) {
          mentionedCustomerIds.add(customerId);
        }
        match = mentionRegex.exec(content);
      }

      return Array.from(mentionedCustomerIds);
    },
    async handleSubmit() {
      if (!this.noteText.trim()) {
        return;
      }

      try {
        // Get transformed content from AddInternalNoteForm
        const transformedContent = this.$refs.addNoteForm?.getTransformedContent() || this.noteText;
        const mentionedCustomerIds = this.extractMentionedCustomerIds(transformedContent);

        const data = {
          content: transformedContent,
          mentioned_customer_ids: mentionedCustomerIds
        };

        // Call API to create note
        await createInternalNoteApi(this.patientId, data);

        // Success notification
        notify({
          title: 'Success',
          message: 'Internal Note Added Successfully',
          type: 'success',
          duration: 3000
        });

        // Clear input
        this.noteText = '';

        // Refresh notes list
        await this.fetchNotes();

        // Emit event for parent
        this.$emit('submit', this.noteText);

      } catch (error) {
        console.error('Failed to add internal note:', error);
        notify({
          title: 'Error',
          message: 'Failed to Add Internal Note',
          type: 'error',
          duration: 3000
        });
      }
    },
  },
};
</script>

<style>
.internal-notes-body {
  padding: 20px 0;
  background-color: #F9FBFD;
}

/* Allow expanded note cards to spill outside the dialog body without being clipped. */
.general-dialog-body:has(.history-notes-scroll-container) {
  overflow: visible !important;
}

.history-notes-title {
  color: var(--TrueBlack, #333);
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
  letter-spacing: 0.15px;
}

.history-notes-section {
  position: relative;
  z-index: 10;
}

.history-notes-scroll-container {
  display: flex;
  gap: 12px;
  overflow-x: auto !important;
  overflow-y: visible !important;
  padding: 10px 10px 20px 10px;
  position: relative;
  scrollbar-width: none;
}

.history-notes-scroll-container.card-expanded {
  padding-bottom: 500px !important;
  margin-bottom: -480px;
}

/* When there's only one card and it's expanded, add extra 20px to compensate */
.history-notes-scroll-container.card-expanded.single-card {
  margin-bottom: -460px;
}

.history-notes-scroll-container::-webkit-scrollbar {
  display: none;
}

.internal-notes-divider {
  margin: 8px 0 16px 0;
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
}
</style>
